import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationDropdown } from '../notification/NotificationDropdown';
import Submenu from './Submenu';
import UserProfileMenu from './UserProfileMenu';
import ChatbotWidget from '../chatbot/ChatbotWidget';
import { ChatbotPageContext } from '../../services/chatbotAPI';

interface StudentLayoutProps {
    children: React.ReactNode;
    chatbotPageContext?: ChatbotPageContext;
    chatbotContextActivityId?: number | null;
    chatbotContextArticleSlug?: string | null;
}

const StudentLayout: React.FC<StudentLayoutProps> = ({
    children,
    chatbotPageContext = 'GLOBAL',
    chatbotContextActivityId = null,
    chatbotContextArticleSlug = null,
}) => {
    const { username, logout, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const menuItems = [
        {
            name: 'Dashboard',
            href: '/dashboard',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
            ),
        },
        {
            name: 'Lịch sử tham gia',
            href: '/student/participation-history',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
        },
        {
            name: 'Nhiệm vụ',
            href: '/student/tasks',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
            ),
        },
        {
            name: 'Công tác chuẩn bị',
            href: '/student/preparation',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-3-3v6m9-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
            ),
        },
        {
            name: 'Quét QR Check-in',
            href: '/student/qr-checkin',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
            ),
        },
        {
            name: 'Hồ sơ',
            href: '/student/profile',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
        },
    ];

    const eventsSubmenu = {
        title: 'Sự kiện',
        icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        ),
        items: [
            {
                name: 'Sự kiện',
                href: '/student/events',
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                ),
            },
            {
                name: 'Chuỗi sự kiện',
                href: '/student/series',
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                ),
            },
            {
                name: 'Mini Game',
                href: '/student/minigames',
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                ),
            },
        ],
    };

    const articlesSubmenu = {
        title: 'Bài viết',
        icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h10l6 6v10a2 2 0 01-2 2zM14 3v6h6" />
            </svg>
        ),
        items: [
            {
                name: 'Bài viết',
                href: '/articles',
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h10l6 6v10a2 2 0 01-2 2zM14 3v6h6" />
                    </svg>
                ),
            },
            {
                name: 'Bài viết đã lưu',
                href: '/wishlist',
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                ),
            },
        ],
    };

    const scoresSubmenu = {
        title: 'Điểm số',
        icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
        ),
        items: [
            {
                name: 'Điểm số',
                href: '/student/scores',
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                ),
            },
            {
                name: 'Khiếu nại điểm',
                href: '/student/scores/appeals',
                icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                ),
            },
        ],
    };

    const isActive = (href: string) => {
        if (href === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname.startsWith(href);
    };

    const getPageTitle = () => {
        const path = location.pathname;
        if (path.startsWith('/student/calendar')) return 'Lịch của tôi';
        if (path.startsWith('/student/scores/appeals')) return 'Khiếu nại điểm';
        if (path === '/student/scores' || path.startsWith('/student/scores/')) return 'Điểm số';
        if (path.startsWith('/student/series')) return 'Chuỗi sự kiện';
        if (path.startsWith('/student/minigames')) return 'Mini Game';
        if (path.startsWith('/student/events')) return 'Sự kiện';
        if (path.startsWith('/wishlist')) return 'Bài viết đã lưu';
        if (path.startsWith('/articles')) return 'Bài viết';
        return menuItems.find((item) => isActive(item.href))?.name || 'Dashboard';
    };

    const headerTitle = getPageTitle();
    const dashboardItem = menuItems[0];
    const afterEventsItems = menuItems.slice(1, 3); // Lịch sử tham gia, Nhiệm vụ
    const afterArticlesItems = menuItems.slice(3, 4); // Công tác chuẩn bị
    const bottomMenuItems = menuItems.slice(4); // QR, Hồ sơ

    const renderMenuLink = (item: { name: string; href: string; icon: React.ReactNode }) => {
        const active = isActive(item.href);
        return (
            <li key={item.name}>
                <Link
                    to={item.href}
                    className={`sidebar-link flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        active
                            ? 'bg-[#FFD66D] bg-opacity-20 text-[#FFD66D] border-l-4 border-[#FFD66D]'
                            : 'text-gray-300 hover:bg-[#002A66] hover:text-white border-l-4 border-transparent'
                    }`}
                >
                    <span className={`${sidebarOpen ? 'mr-3' : 'mx-auto'}`}>{item.icon}</span>
                    {sidebarOpen && <span>{item.name}</span>}
                </Link>
            </li>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <aside
                className={`bg-[#001C44] text-white transition-all duration-300 ease-in-out ${
                    sidebarOpen ? 'w-64' : 'w-20'
                } fixed h-screen z-30`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo/Brand */}
                    <div className="h-16 flex items-center justify-between px-4 border-b border-[#002A66]">
                        <Link to="/" className="text-xl font-bold text-[#FFD66D] hover:opacity-80 transition-opacity">
                            {sidebarOpen ? 'CampusLife' : 'CL'}
                        </Link>
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="text-white hover:text-[#FFD66D] transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {sidebarOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                )}
                            </svg>
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 overflow-y-auto py-4">
                        <ul className="space-y-1 px-2">
                            {renderMenuLink(dashboardItem)}

                            <Submenu
                                title={eventsSubmenu.title}
                                icon={eventsSubmenu.icon}
                                items={eventsSubmenu.items}
                                sidebarOpen={sidebarOpen}
                            />

                            {afterEventsItems.map(renderMenuLink)}

                            <Submenu
                                title={articlesSubmenu.title}
                                icon={articlesSubmenu.icon}
                                items={articlesSubmenu.items}
                                sidebarOpen={sidebarOpen}
                            />

                            {afterArticlesItems.map(renderMenuLink)}

                            <Submenu
                                title={scoresSubmenu.title}
                                icon={scoresSubmenu.icon}
                                items={scoresSubmenu.items}
                                sidebarOpen={sidebarOpen}
                            />

                            {bottomMenuItems.map(renderMenuLink)}
                        </ul>
                    </nav>

                    <div className="border-t border-[#002A66] p-4 space-y-2">
                        {(user?.role === 'ADMIN' || user?.role === 'MANAGER') && (
                            <Link
                                to={user?.role === 'ADMIN' ? '/admin/articles' : '/manager/articles'}
                                className="flex items-center rounded-lg px-4 py-3 text-sm font-medium text-gray-300 transition-all duration-200 hover:bg-[#002A66] hover:text-white"
                            >
                                <span className={`${sidebarOpen ? 'mr-3' : 'mx-auto'}`}>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                </span>
                                {sidebarOpen && <span>Quản lý bài viết</span>}
                            </Link>
                        )}
                    </div>

                    {/* User Info */}
                    <div className="border-t border-[#002A66] p-4">
                        <UserProfileMenu sidebarOpen={sidebarOpen} />
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
                {/* Header */}
                <header className="bg-white shadow-sm sticky top-0 z-20 border-b border-gray-200">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-[#001C44]">
                                    {headerTitle}
                                </h2>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Link
                                    to="/student/calendar"
                                    className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold transition-all active:scale-[0.98] ${
                                        location.pathname.startsWith('/student/calendar')
                                            ? 'bg-[#001C44] text-[#FFD66D]'
                                            : 'bg-gray-50 text-[#001C44] hover:bg-gray-100'
                                    }`}
                                    title="Lịch của tôi"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="hidden sm:inline">Lịch của tôi</span>
                                </Link>
                                <NotificationDropdown />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-6">
                    {children}
                </main>

                {/* Footer */}
                <footer className="bg-white border-t border-gray-200 mt-auto">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between text-sm text-gray-600">
                            <p>&copy; 2024 CampusLife. All rights reserved.</p>
                            <p className="text-[#001C44] font-medium">Hệ thống quản lý hoạt động sinh viên</p>
                        </div>
                    </div>
                </footer>

                <ChatbotWidget
                    pageContext={chatbotPageContext}
                    contextActivityId={chatbotContextActivityId}
                    contextArticleSlug={chatbotContextArticleSlug}
                />
            </div>
        </div>
    );
};

export default StudentLayout;

